import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { FileTextIcon, BookOpenIcon, LayersIcon, ZapIcon, MonitorSmartphoneIcon, EyeIcon } from 'lucide-react';
const GOLD = '#d8b878';
const ITEMS = [{
  icon: FileTextIcon,
  text: 'Ebook completo en PDF.'
}, {
  icon: BookOpenIcon,
  text: '118 páginas de lectura profunda.'
}, {
  icon: LayersIcon,
  text: '13 capítulos + epílogo.'
}, {
  icon: ZapIcon,
  text: 'Acceso inmediato después de completar el pago.'
}, {
  icon: MonitorSmartphoneIcon,
  text: 'Lectura desde celular, tablet o computadora.'
}, {
  icon: EyeIcon,
  text: 'Una experiencia diseñada para cuestionar creencias, patrones y formas de vivir en automático.'
}];
export function WhatYouGet() {
  const container = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.09
      }
    }
  };
  const item = {
    hidden: {
      opacity: 0,
      y: 22
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };
  return <section className="relative overflow-hidden bg-matrix-black py-20 md:py-28 px-5 md:px-8" data-id="element-328">
      {/* Subtle digital texture + depth */}
      <div className="pointer-events-none absolute inset-0 z-0 opacity-[0.04]" style={{
      backgroundImage: 'repeating-linear-gradient(90deg, rgba(0,255,65,0.6) 0px, rgba(0,255,65,0.6) 1px, transparent 1px, transparent 10px)',
      maskImage: 'radial-gradient(ellipse at center, black 25%, transparent 80%)',
      WebkitMaskImage: 'radial-gradient(ellipse at center, black 25%, transparent 80%)'
    }} data-id="element-329"></div>
      <div className="pointer-events-none absolute inset-0 z-0 bg-[radial-gradient(ellipse_at_top,_rgba(216,184,120,0.06)_0%,_transparent_55%)]" data-id="element-330"></div>
      {/* Ambient depth rising from the bottom + soft transition in */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-0 h-2/3 bg-[radial-gradient(ellipse_at_bottom,_rgba(0,255,65,0.07)_0%,_transparent_60%)]" data-id="element-331"></div>
      <div className="pointer-events-none absolute inset-x-0 top-0 z-0 h-24 bg-gradient-to-b from-matrix-black to-transparent" data-id="element-332"></div>

      <div className="max-w-5xl mx-auto relative z-10" data-id="element-333">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.8
      }} className="text-center mb-12 md:mb-16 max-w-2xl mx-auto" data-id="element-334">
          <span className="inline-block font-inter text-[10px] md:text-xs tracking-[0.4em] uppercase mb-5" style={{
          color: 'rgba(216,184,120,0.7)'
        }} data-id="element-335">
            Tu acceso
          </span>
          <h2 className="font-cinzel text-3xl md:text-4xl lg:text-5xl text-matrix-cream mb-6 drop-shadow-[0_0_12px_rgba(245,230,200,0.12)]" data-id="element-336">
            ¿Qué recibes con tu compra?
          </h2>
          <p className="text-[15px] md:text-lg text-matrix-white/80 font-light leading-[1.9]" data-id="element-337">
            Al completar tu compra, recibirás acceso inmediato a una lectura
            profunda diseñada para ayudarte a cuestionar creencias, observar
            patrones y mirar tu realidad desde una perspectiva más consciente.
          </p>
        </motion.div>

        {/* Benefit cards */}
        <motion.div variants={container} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-50px'
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5" data-id="element-338">
          {ITEMS.map(({
          icon: Icon,
          text
        }, i) => <motion.div key={i} variants={item} whileHover={{
          y: -4,
          boxShadow: '0 0 32px -10px rgba(216,184,120,0.38)'
        }} className="group flex flex-row sm:flex-col items-center sm:items-start gap-3 sm:gap-4 rounded-xl border border-matrix-green/15 bg-matrix-dark/50 backdrop-blur-sm p-3.5 sm:p-5 md:p-6 transition-colors duration-300 hover:border-[rgba(216,184,120,0.4)]" data-id="element-339">
              <span className="shrink-0 flex h-9 w-9 sm:h-11 sm:w-11 items-center justify-center rounded-lg border border-[rgba(216,184,120,0.22)] bg-matrix-green/[0.06] transition-colors group-hover:border-[rgba(216,184,120,0.5)]" style={{
            color: GOLD
          }} data-id="element-340">
                <Icon className="w-[18px] h-[18px] sm:w-5 sm:h-5" strokeWidth={1.5} data-id="element-341" />
              </span>
              <p className="text-matrix-white/85 text-sm sm:text-[14.5px] md:text-base font-light leading-snug sm:leading-relaxed" data-id="element-342">
                {text}
              </p>
            </motion.div>)}
        </motion.div>

        {/* Closing phrase */}
        <motion.p initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8,
        delay: 0.2
      }} className="text-center mt-12 md:mt-16 max-w-2xl mx-auto font-cinzel text-lg sm:text-xl md:text-2xl leading-[1.5] text-matrix-cream" style={{
        textShadow: '0 0 22px rgba(216,184,120,0.18)'
      }} data-id="element-343">
          Todo en un formato digital{' '}
          <span style={{
          color: GOLD
        }} data-id="element-344">
            simple, directo y fácil de leer
          </span>{' '}
          desde cualquier dispositivo.
        </motion.p>
      </div>
    </section>;
}